
import { defineComponent, PropType } from "vue";
// DatasetStatus is only used in the <template>, which eslint doesn't include
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { DatasetStatus, IVisHierarchy, ParaviewFilterPlus } from "@/types/dataset";
import { useActiveStore } from "@/stores/ssaStore/active";
import { useFiltersStore } from "@/stores/ssaStore/filters";
import { storeToRefs } from "pinia";
import { useDisplayPaneStore } from "@/stores/ssaStore/displayPane";
import { useLayerStore } from "@/stores/ssaStore/layer";
import DatasetPropertiesAndLogs from "./datasetPropertiesAndLogs.vue";

export default defineComponent({
	name: "imdex-dataset",
	components: { DatasetPropertiesAndLogs },
	setup() {
		const { SSA_ACTIVE_LAYER: activeLayer } = storeToRefs(useActiveStore());
		const { layerStates } = storeToRefs(useLayerStore());
		const { view: activeView } = storeToRefs(useActiveStore());
		const { displayStates } = storeToRefs(useDisplayPaneStore());
		const filtersStore = useFiltersStore();
		const { SSA_ACTIVE_FILTERS_MAP_BY_DATASET: activeFiltersMap } = storeToRefs(
			useFiltersStore()
		);

		return {
			activeLayer,
			layerStates,
			activeView,
			filtersStore,
			displayStates,
			activeFiltersMap
		};
	},
	props: {
		id: {
			type: String,
			default: ""
		},
		name: {
			type: String,
			default: "NO DEFAULT"
		},
		isDatasetTop: {
			type: Boolean,
			default: false
		},
		status: {
			type: String,
			default: ""
		},
		dataType: {
			type: String,
			default: ""
		},
		groupName: {
			type: String,
			default: ""
		},
		isGAS: {
			type: Boolean,
			default: false
		},
		tree: {
			type: Object as PropType<string[]>,
			default: () => [] as string[],
			required: false
		},
		selected: {
			type: Object as PropType<string[]>,
			default: () => [] as string[]
		},
		highlighted: {
			type: Object as PropType<string[]>,
			default: () => [] as string[]
		},
		searchTerm: {
			type: String,
			default: ""
		},
		paraviewVisHierarchies: {
			type: Object as PropType<string[]>,
			default: () => [] as string[]
		},
		hierarchyDepth: {
			type: Number,
			default: 1
		}
	},
	data() {
		return {
			isExpanded: false,
			showMenu: false,
			hasHighlight: false,
			datasets: [] as any,
			selectType: "on",
			closeOptionsMenuListener: null as any,
			drillhole: require("@/assets/svg/hole_type2.svg"),
			blockmodel: require("@/assets/svg/bm_type.svg"),
			points: require("@/assets/svg/polygon_type.svg"),
			surface: require("@/assets/svg/shape.svg"),
			ioGAS: require("@/assets/svg/ioGAS_web.svg"),
			renaming: false,
			newName: this.name,
			visHierarchy: [] as IVisHierarchy[],
			treeParsed: false,
			selectedChildren: [] as string[], // uniqueCellId string[]
			showDatasetNameTruncatedTooltip: false, // whether this dataset's name is truncated due to size constraints
			filtersIconClicked: false,
			datasetPropertiesOpen: false
		};
	},
	mounted() {
		this.parseTree();
		this.drawDashedBorders();
	},
	beforeUnmount() {
		if ((this.$refs as any).showMenu) {
			this.toggleShowMenu("remove");
		}
	},
	computed: {
		hasClip() {
			const clippedLayers = this.associatedLayers.filter((item: any) => {
				return item.clip.active;
			});
			return clippedLayers.length > 0;
		},
		associatedLayers(): any {
			if (
				this.layerStates[this.activeView] &&
				Object.keys(this.layerStates[this.activeView]).length
			) {
				const layers = Object.keys(this.layerStates[this.activeView]).filter(
					(item: any) => item.indexOf(this.id) > -1
				);
				const assLayers = [];
				for (let i = 0; i < layers.length; i++) {
					assLayers.push(this.layerStates[this.activeView][layers[i]]);
				}
				return assLayers;
			} else {
				return [];
			}
		},
		activeLayerName(): any {
			return this.activeLayer; // this.activeLayers ->
		},
		cellUniqueId() {
			let hierarchyStringSoFar = "";
			// all instances should already be filtered, so we can assume this is defined/valid, but it only has a value once we're past the top level
			if (this.paraviewVisHierarchies.length >= 1) {
				const hierarchyString = this.paraviewVisHierarchies[0];
				const hierarchyStringSplit = hierarchyString.split("::");
				hierarchyStringSoFar = hierarchyStringSplit
					.slice(0, this.hierarchyDepth)
					.join("::");
			}

			let res = this.id;

			if (hierarchyStringSoFar) res += ".." + hierarchyStringSoFar;
			return res;
		},
		isHighlighted() {
			return this.highlighted.indexOf(this.cellUniqueId) > -1;
		},
		isSearching() {
			return this.searchTerm.length > 0;
		},
		selectCheck() {
			let check: "all" | "indeterminate" | "none" = "none";
			let hierarchyStringMatches = 0;
			for (const selection of this.selected) {
				if (selection == this.id) return "all"; // if the top level dataset is directly selected, all subselections of the dataset are selected
				if (selection == this.cellUniqueId) return "all"; // if this is directly selected

				const uniqueCellIDSplit = selection.split("..");
				// check if this is a file from the same dataset, and stop checking this selection if it is not
				if (!(this.id == uniqueCellIDSplit[0])) {
					continue;
				}

				// if the file is in the same dataset, check it's hierarchies
				const selectionHierarchyString = uniqueCellIDSplit[1];
				const potentiallyVisualizableHierarchiesCount = this.paraviewVisHierarchies.length;

				for (const hierarchyString of this.paraviewVisHierarchies) {
					const split = hierarchyString.split("::"); // split the hierarchy item into its parts
					const hSplit = selectionHierarchyString.split("::"); //split the unique string into parts to cross check
					if (hSplit.length == 1 && hSplit[0] == "ROOT") {
						//
						return "all";
					} else {
						let fullMatch = true; // assumed to be true, unless one item doesn't match
						// iterate through the hierarchy, and if all
						for (let s = 1; s < hSplit.length; s++) {
							// loop through and check if each individual item matches, and if any don't match, don't select this item
							if (hSplit[s] != split[s]) {
								fullMatch = false;
							}
						}
						if (fullMatch) {
							hierarchyStringMatches++;
						}
					}
				}
				// if all of the hierarchies this selection could visualize are visualized, check the checkbox
				if (hierarchyStringMatches >= potentiallyVisualizableHierarchiesCount) return "all";
			}
			// if only some of the hierarchies match, partially check the checkbox
			if (hierarchyStringMatches > 0) {
				check = "indeterminate";
			}
			return check;
		},
		highlightCheck() {
			// || (this.ofHighlighted.length == this.tree.length && this.tree.length > 0)
			if (this.highlighted.indexOf(this.cellUniqueId) > -1) {
				return "all";
			} else if (this.highlighted.indexOf(this.cellUniqueId) == 0) {
				return "none";
			} else {
				return "indeterminate";
			}
		},
		statusTextWithErrorMsg() {
			return this.status === ("Failed" as DatasetStatus)
				? this.$t("ssa.hierarchy.dataset.errorLoading")
				: this.status;
		},
		activeFiltersForDataset() {
			return (
				this.isDatasetTop &&
				this.activeFiltersMap?.[this.activeView]?.[this.id]?.aggregatedFilters.filter(
					(filter: ParaviewFilterPlus) =>
						!(
							filter.disableButDisplayFilter || filter.disableButDisplayFilterSlashEye
						) &&
						this.displayStates[this.activeView] && // ensure the filter's layer is selected in the display pane
						this.id &&
						this.displayStates[this.activeView][this.id]?.find(
							(item: any) =>
								item.layerName == filter.layerName && item.selected === true
						)
				).length > 0
			);
		}
	},
	methods: {
		drawDashedBorders() {
			this.$nextTick(() => {
				const el: HTMLDivElement = this.$refs["dataset-name"] as HTMLDivElement;
				if (el != null) {
					for (const child of el.children) {
						if (child.classList.contains("dashed-hierarchy-border")) {
							el.removeChild(child);
						}
					}
				}
				const dashedBorderEl = document.createElement("div");
				dashedBorderEl.classList.add("dashed-hierarchy-border");
				if (this.datasets.length == 0) {
					dashedBorderEl.style.width = "15px";
				} else {
					dashedBorderEl.style.width = "8px";
				}
				if (el != null) {
					el.appendChild(dashedBorderEl);
				}
				if (this.searchTerm.length > 0) {
					this.isExpanded = true;
					this.highlight(this.searchTerm);
				}
			});
		},
		toggleRename() {
			this.renaming = true;
			this.$nextTick(() => {
				(this.$refs as any).renameInput.focus();
			});
		},
		rightClickEmitHighlight() {
			// Cover cases where right clicking another dataset other than the one highlighted will still refer to the highlighted one
			if (!this.highlighted.includes(this.cellUniqueId)) {
				this.emitClearHighlights();
				this.emitHighlight();
			}
		},
		toggleShowMenu(type?: string, e?: any) {
			if (type == "right") {
				const [y, x] = [e.pageY, e.pageX];
				this.showMenu = !this.showMenu;
				this.$nextTick(() => {
					if (this.showMenu) {
						// const {top, left} = (this.$refs as any).showMenuIcon.getBoundingClientRect();
						const body = document.getElementsByTagName("body")[0];
						(this.$refs as any).showMenu.style.top = y + 5 + "px";
						(this.$refs as any).showMenu.style.left = x + "px";
						body.appendChild((this.$refs as any).showMenu);
					}
				});
			} else if (type == "remove") {
				const body = document.getElementsByTagName("body")[0];

				body.removeChild((this.$refs as any).showMenu);
			} else {
				this.showMenu = !this.showMenu;
				this.$nextTick(() => {
					if (this.showMenu) {
						const { top, left } = (
							this.$refs as any
						).showMenuIcon.getBoundingClientRect();
						const body = document.getElementsByTagName("body")[0];
						(this.$refs as any).showMenu.style.top = top + 20 + "px";
						(this.$refs as any).showMenu.style.left = left + "px";
						body.appendChild((this.$refs as any).showMenu);
					}
				});
			}
		},
		highlight(text: any) {
			this.$nextTick(() => {
				let innerHTML =
					this.name.toLowerCase().charAt(0) + this.name.toLowerCase().slice(1);
				const index = innerHTML.indexOf(text.toLowerCase());
				if (index >= 0) {
					this.hasHighlight = true;
					this.$nextTick(async () => {
						const inputText: any = this.$refs["datasetName"];
						if (innerHTML == null) return;
						if (inputText == null) return;
						innerHTML =
							innerHTML.substring(0, index) +
							`<span class='highlight'>` +
							innerHTML.substring(index, index + text.length) +
							"</span>" +
							innerHTML.substring(index + text.length);
						inputText.innerHTML = innerHTML;
					});
				} else {
					this.hasHighlight = false;
				}
			});
		},
		emitSetDefaultMoveGroup() {
			this.$emit("setDefaultMoveGroup", this.groupName);
		},
		emitSetActiveDatasetType() {
			this.$emit("setActiveDatasetType", this.dataType);
		},
		emitSetActiveGroup() {
			this.$emit("setActiveGroup", this.groupName);
		},
		emitDeleteDataset(data: any) {
			this.$emit("deleteDataset", data);
		},
		emitMoveDataset(data: any) {
			this.emitSetDefaultMoveGroup();
			this.$emit("moveDataset", data);
		},
		emitUpdateBlastDogDataset() {
			this.$emit("updateBlastDogDataset", this.dataType.toLowerCase());
		},
		closeRenameInput() {
			this.renaming = false;
		},
		emitRenameDataset(data?: any) {
			this.renaming = false;

			if (!this.newName || !this.newName.trim()) this.newName = this.name; // if empty, reassign to old name as we'll ignore the input regardless
			if (data != null) {
				this.$emit("renameDataset", data);
			} else {
				this.$emit("renameDataset", {
					data: {
						datasetId: this.id,
						name: this.newName,
						datasetName: this.name
					}
				});
			}
		},
		// we can select any dataset, or hierarchies below it // for hierarchies below a dataset, the emit is passed up recursively
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		emitSelect(data?: {
			cellUniqueId: string;
			paraviewVisHierarchies: string[];
			handleUnselectedChild?: boolean;
		}) {
			if (data) {
				this.$emit("select", { ...data, dataType: this.dataType });
			} else {
				const type = this.selectCheck === "all" && !data ? "off" : "on";

				this.$emit("select", {
					cellUniqueId: this.cellUniqueId,
					paraviewVisHierarchies: this.paraviewVisHierarchies,
					type,
					handleUnselectedChild: type == "off",
					dataType: this.dataType
				});
			}
		},
		emitHighlight({ type }: { type?: string } = {}) {
			this.$emit("highlight", {
				datasetId: this.cellUniqueId,
				type,
				dataType: this.dataType
			});
			if (this.filtersIconClicked) {
				this.filtersStore.SSA_FILTERS_ICON_CLICKED();
				this.filtersIconClicked = false;
			}
		},
		emitClearHighlights() {
			this.$emit("clearHighlights");
		},
		parseTree() {
			this.treeParsed = false;
			const visHierarchy: IVisHierarchy[] = [];

			// This is the names of the next levels cells
			const subSelections: {
				[key: string]: { tree: string[]; paraviewVisHierarchies: string[] };
			} = {};

			// Create the tree from the list of string options provided
			for (const hierarchy of this.paraviewVisHierarchies) {
				const name = hierarchy.split("::")?.[this.hierarchyDepth];
				if (!name) continue;
				subSelections[name] = subSelections[name] || {
					tree: [],
					paraviewVisHierarchies: []
				};
				subSelections[name].tree.push(
					hierarchy
						.split("::")
						.slice(this.hierarchyDepth + 1)
						.join("::")
				);
				subSelections[name].paraviewVisHierarchies.push(hierarchy);
			}
			for (const entry of Object.entries(subSelections)) {
				visHierarchy.push({
					name: entry[0],
					tree: entry[1].tree,
					paraviewVisHierarchies: entry[1].paraviewVisHierarchies
				});
			}

			this.visHierarchy = visHierarchy;
			this.treeParsed = true;
		},
		checkShowDsNameTooltip(event: MouseEvent): void {
			if (!this.isDatasetTop) return;
			const datasetN: HTMLElement = event.target as HTMLElement;
			this.showDatasetNameTruncatedTooltip = this.isDatasetNameTruncated(datasetN);
		},
		isDatasetNameTruncated(element: HTMLElement): boolean {
			return element.offsetWidth < element.scrollWidth;
		},
		selectRenameText(event: FocusEvent): void {
			this.newName = this.name; // reassign newName to avoid incorrect values on previously submitted errors
			(event?.target as HTMLInputElement)?.select();
		}
	},
	watch: {
		showMenu(v) {
			if (v) {
				this.$nextTick(() => {
					setTimeout(() => {
						window.addEventListener(
							"mousedown",
							(this.closeOptionsMenuListener = () => {
								this.showMenu = false;
								window.removeEventListener(
									"mousedown",
									this.closeOptionsMenuListener
								);
							})
						);
					}, 100);
				});
			}
		},
		searchTerm(v) {
			if (v.length > 0) {
				this.isExpanded = true;
				this.$nextTick(() => {
					this.highlight(v);
				});
			} else {
				this.isExpanded = false;
			}
		},
		tree: {
			deep: true,
			handler() {
				this.parseTree();
				this.drawDashedBorders();
			}
		}
	}
});
